import React, { useEffect, useRef, useState } from 'react'
import {
  Typography,
  Button,
  Breadcrumb,
  Layout,
  Flex,
  Card,
  Form,
  Input,
  Select,
  Spin,
  Table,
  message,
} from 'antd'
import type { TableProps } from 'antd'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  HolderOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import type { CampaignTemplate } from '@/data'
import { mockCampaigns, dispositionOptions } from '@/data/smsRemarketing'
// import { getCampaign } from '@/api/lop/campaigns/getCampaign'
// import { createCampaign } from '@/api/lop/campaigns/createCampaign'
// import {
//   updateCampaign,
//   type UpdateCampaignPayload,
// } from '@/api/lop/campaigns/updateCampaign'
// import { deleteTemplate } from '@/api/lop/campaigns/templates/deleteTemplate'

const { Title } = Typography
const { Content } = Layout

const SmsRemarketingEditorContent: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditMode = Boolean(id)

  const [loading, setLoading] = useState(isEditMode)
  const [saving, setSaving] = useState(false)
  const [campaignName, setCampaignName] = useState('')
  // const [campaignType, setCampaignType] = useState('')
  const [selectedDispositions, setSelectedDispositions] = useState<number[]>([])
  const [templates, setTemplates] = useState<CampaignTemplate[]>([])

  const hasFetched = useRef(false)
  const dragItem = useRef<number | null>(null)
  const dragOverItem = useRef<number | null>(null)

  useEffect(() => {
    if (!id || hasFetched.current) return
    hasFetched.current = true

    const campaign = mockCampaigns.find((c) => String(c.id) === id)
    if (campaign) {
      setCampaignName(campaign.name)
      setSelectedDispositions(campaign.crm_disposition_mapping_ids ?? [])
      const sorted = [...(campaign.campaign_templates ?? [])].sort(
        (a, b) => a.ordinal - b.ordinal,
      )
      setTemplates(sorted)
    }
    setLoading(false)
  }, [id])

  // const fetchCampaign = useCallback(async () => {
  //   if (!id) return
  //   try {
  //     setLoading(true)
  //     const campaign = await getCampaign(id)
  //     if (campaign) {
  //       setCampaignName(campaign.name)
  //       setCampaignType(campaign.campaign_type ?? '')
  //       setSelectedDispositions(campaign.crm_disposition_mapping_ids ?? [])
  //       const sorted = [...(campaign.campaign_templates ?? [])].sort(
  //         (a, b) => a.ordinal - b.ordinal,
  //       )
  //       setTemplates(sorted)
  //     }
  //   } catch (err) {
  //     message.error('Failed to load campaign')
  //     console.error(err)
  //   } finally {
  //     setLoading(false)
  //   }
  // }, [id])
  //
  // useEffect(() => {
  //   if (!id || hasFetched.current) return
  //   hasFetched.current = true
  //   fetchCampaign()
  // }, [id, fetchCampaign])

  const handleDragStart = (index: number) => {
    dragItem.current = index
  }

  const handleDragEnter = (index: number) => {
    dragOverItem.current = index
  }

  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null) return
    if (dragItem.current === dragOverItem.current) {
      dragItem.current = null
      dragOverItem.current = null
      return
    }

    setTemplates((prev) => {
      const updated = [...prev]
      const dragged = updated.splice(dragItem.current!, 1)[0]
      updated.splice(dragOverItem.current!, 0, dragged)
      return updated
    })

    dragItem.current = null
    dragOverItem.current = null
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleSave = async () => {
    if (!campaignName.trim()) {
      message.warning('Please enter a campaign name')
      return
    }
    try {
      setSaving(true)

      await new Promise((r) => setTimeout(r, 500))
      if (isEditMode) {
        message.success('Campaign saved')
      } else {
        message.success('Campaign created')
        navigate('/sms-remarketing')
      }

      // const campaignPayload: UpdateCampaignPayload = {
      //   name: campaignName,
      //   campaign_type: campaignType || 'sms',
      //   ...(selectedDispositions.length > 0 && {
      //     crm_disposition_mapping_ids: selectedDispositions,
      //   }),
      // }
      //
      // if (isEditMode && id) {
      //   if (templates.length > 0) {
      //     campaignPayload.campaign_templates = templates.map((t, i) => ({
      //       id: t.id,
      //       ordinal: i + 1,
      //     }))
      //   }
      //   await updateCampaign(id, campaignPayload)
      //   message.success('Campaign saved')
      // } else {
      //   const created = await createCampaign({
      //     name: campaignName,
      //     campaign_type: campaignType || 'sms',
      //     ...(selectedDispositions.length > 0 && {
      //       crm_disposition_mapping_ids: selectedDispositions,
      //     }),
      //   })
      //   message.success('Campaign created')
      //   navigate(`/sms-remarketing/${created.id}/edit`)
      // }
    } catch {
      message.error(
        isEditMode ? 'Failed to save campaign' : 'Failed to create campaign',
      )
      // console.error(err)
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteTemplate = (templateId: number) => {
    setTemplates((prev) => prev.filter((t) => t.id !== templateId))
    message.success('Template deleted')
    // if (!id) return
    // try {
    //   await deleteTemplate(id, String(templateId))
    //   setTemplates((prev) => prev.filter((t) => t.id !== templateId))
    //   message.success('Template deleted')
    // } catch {
    //   message.error('Failed to delete template')
    // }
  }

  const templateColumns: TableProps<CampaignTemplate>['columns'] = [
    {
      title: '',
      key: 'drag',
      width: 40,
      render: (_, __, index) => (
        <HolderOutlined
          draggable
          onDragStart={() => handleDragStart(index)}
          onDragEnd={handleDragEnd}
          style={{ cursor: 'grab', color: '#999', fontSize: 16 }}
        />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Link to={`/sms-remarketing/${id}/templates/${record.id}/edit`}>
          {text}
        </Link>
      ),
    },
    {
      title: 'Text',
      dataIndex: 'text',
      key: 'text',
      ellipsis: true,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value: string) =>
        value
          ? new Date(value).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Flex gap="middle">
          <Link to={`/sms-remarketing/${id}/templates/${record.id}/edit`}>
            <EditOutlined style={{ fontSize: 16 }} />
          </Link>
          <DeleteOutlined
            style={{ fontSize: 16, color: '#ff4d4f', cursor: 'pointer' }}
            onClick={() => handleDeleteTemplate(record.id)}
          />
        </Flex>
      ),
    },
  ]

  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    )
  }

  return (
    <div>
      <Flex vertical gap="middle" style={{ width: '100%' }}>
        <Breadcrumb
          items={[
            { title: 'SMS Remarketing', href: '/sms-remarketing' },
            {
              title: isEditMode ? `Edit: ${campaignName}` : 'New Campaign',
            },
          ]}
        />

        <Flex align="center" gap="middle">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/sms-remarketing')}
          />
          <Title level={3} style={{ margin: 0 }}>
            {isEditMode
              ? `Edit Campaign: ${campaignName}`
              : 'Create New Campaign'}
          </Title>
        </Flex>

        <Flex gap="large" style={{ marginTop: 16 }}>
          <Content style={{ flex: 1, minHeight: 400 }}>
            <Card title="Campaign Info" style={{ maxWidth: 1200 }}>
              <Form layout="vertical">
                <Form.Item label="Campaign Name">
                  <Input
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    placeholder="Enter campaign name"
                  />
                </Form.Item>
                <Form.Item label="Dispositions">
                  <Select
                    mode="multiple"
                    value={selectedDispositions}
                    onChange={setSelectedDispositions}
                    options={dispositionOptions}
                    placeholder="Select dispositions for this campaign"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Form>
            </Card>

            {isEditMode && (
              <Card
                title="Text Message Templates"
                style={{ maxWidth: 1200, marginTop: 16 }}
                extra={
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() =>
                      navigate(
                        `/sms-remarketing/${id}/templates/new?ordinal=${templates.length + 1}`,
                      )
                    }
                  >
                    Add Template
                  </Button>
                }
              >
                <Table<CampaignTemplate>
                  rowKey="id"
                  columns={templateColumns}
                  dataSource={templates}
                  pagination={false}
                  onRow={(_, index) => ({
                    onDragEnter: () => handleDragEnter(index!),
                    onDragOver: handleDragOver,
                  })}
                />
              </Card>
            )}

            <Flex justify="flex-end" style={{ paddingTop: 16, maxWidth: 1200 }}>
              <Button type="primary" onClick={handleSave} loading={saving}>
                {isEditMode ? 'Save Campaign' : 'Create Campaign'}
              </Button>
            </Flex>
          </Content>
        </Flex>
      </Flex>
    </div>
  )
}

export const SmsRemarketingEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  return <SmsRemarketingEditorContent key={id ?? 'new'} />
}
